import { useEffect, useRef } from "react";
import { personalInfo } from "../data/index";

export default function About() {
  const contentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.2 },
    );

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const cards = [
    {
      icon: "⚡",
      title: "7+ Years",
      desc: "Shipping production-grade software for real users across public and private sectors.",
    },
    {
      icon: "🎯",
      title: "Impact Driven",
      desc: "Built a platform that increased municipal revenue by 45%. Code that moves the needle.",
    },
    {
      icon: "🧠",
      title: "Deep React",
      desc: "useState, useReducer, useEffect, useRef — not just used, but understood from the ground up.",
    },
    {
      icon: "🌍",
      title: "Global Ready",
      desc: "Actively seeking international opportunities to build products that reach millions.",
    },
  ];

  return (
    <section
      id="about"
      style={{
        padding: "120px 24px",
        maxWidth: "1100px",
        margin: "0 auto",
      }}
    >
      <div
        ref={contentRef}
        style={{
          opacity: 0,
          transform: "translateY(40px)",
          transition: "all 0.8s ease",
        }}
      >
        <p
          style={{
            color: "#22d3ee",
            fontSize: "0.85rem",
            fontWeight: 600,
            letterSpacing: "3px",
            textTransform: "uppercase",
            marginBottom: "12px",
          }}
        >
          About Me
        </p>

        <h2
          style={{
            fontSize: "clamp(2rem, 4vw, 2.8rem)",
            fontWeight: 800,
            color: "#f5f5f5",
            marginBottom: "24px",
            letterSpacing: "-1px",
          }}
        >
          The engineer behind the code
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "60px",
            alignItems: "start",
            marginBottom: "80px",
          }}
        >
          <div>
            <p
              style={{
                color: "#a0a0a0",
                fontSize: "1.05rem",
                lineHeight: 1.9,
                marginBottom: "20px",
              }}
            >
              I am a{" "}
              <span style={{ color: "#f5f5f5", fontWeight: 600 }}>
                Software Engineer
              </span>{" "}
              with over 7 years of experience building web applications that
              solve real problems for real people. I started my career in IT
              support, grew into a full programmer role, and have spent the last
              several years specialising in{" "}
              <span style={{ color: "#22d3ee", fontWeight: 600 }}>
                React and modern JavaScript.
              </span>
            </p>
            <p
              style={{
                color: "#a0a0a0",
                fontSize: "1.05rem",
                lineHeight: 1.9,
                marginBottom: "20px",
              }}
            >
              What drives me is not just writing code but building things that{" "}
              <span style={{ color: "#f5f5f5", fontWeight: 600 }}>
                people love to use.
              </span>{" "}
              I think about the user before I write the first component. I think
              about the next engineer before I commit the last line.
            </p>
            <p
              style={{
                color: "#a0a0a0",
                fontSize: "1.05rem",
                lineHeight: 1.9,
              }}
            >
              I hold a BSc in Computer Science and Engineering and I am
              constantly pursuing new knowledge and solidifying existing
              knowledge to attain mastery in the ever changing world of{" "}
              <span style={{ color: "#22d3ee", fontWeight: 600 }}>
                Software Engineering
              </span>{" "}
              because I believe the best engineers never stop learning.
            </p>
          </div>

          <div
            style={{
              background: "#242424",
              borderRadius: "20px",
              padding: "36px",
              border: "1px solid #2e2e2e",
            }}
          >
            <h3
              style={{
                color: "#f5f5f5",
                fontSize: "1.1rem",
                fontWeight: 700,
                marginBottom: "24px",
              }}
            >
              Quick Facts
            </h3>

            {[
              { label: "Location", value: personalInfo.location },
              { label: "Email", value: personalInfo.email },
              { label: "Degree", value: "BSc Computer Science" },
              { label: "Focus", value: "React / JavaScript" },
              { label: "Status", value: "Open to Global Opportunities" },
              { label: "GitHub", value: "github.com/TheNarh" },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "12px 0",
                  borderBottom: "1px solid #2e2e2e",
                }}
              >
                <span
                  style={{
                    color: "#22d3ee",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    minWidth: "80px",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    paddingTop: "2px",
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{
                    color: "#a0a0a0",
                    fontSize: "0.95rem",
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "20px",
          }}
        >
          {cards.map((card) => (
            <div
              key={card.title}
              style={{
                background: "#242424",
                border: "1px solid #2e2e2e",
                borderRadius: "16px",
                padding: "28px 24px",
                transition: "all 0.3s ease",
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#22d3ee";
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 40pxrgba(34, 211, 238),0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#2e2e2e";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <div style={{ fontSize: "2rem", marginBottom: "12px" }}>
                {card.icon}
              </div>
              <h4
                style={{
                  color: "#f5f5f5",
                  fontWeight: 700,
                  fontSize: "1rem",
                  marginBottom: "8px",
                }}
              >
                {card.title}
              </h4>
              <p
                style={{
                  color: "#666",
                  fontSize: "0.85rem",
                  lineHeight: 1.7,
                }}
              >
                {card.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
